import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Loader2, Save } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

export interface Cell {
  id?: string | number;
  versionId?: number;
  probabilityCode: string;
  severityCode: string;
  score: number;
  riskLevelLabel?: string;
  conclusionText?: string;
  colorHex?: string;
}

export interface CellEditorProps {
  cell: Cell | null;
  versionId: number;
  probability?: { code: string; label: string; probabilityValue: number };
  severity?: { code: string; label: string; weight: number };
  isOpen: boolean;
  onClose: () => void;
  onChange: (updatedCell: Cell) => void;
  onSave?: () => void;
}

export function CellEditor({
  cell,
  versionId,
  probability,
  severity,
  isOpen,
  onClose,
  onChange,
  onSave,
}: CellEditorProps) {
  const [editingCell, setEditingCell] = useState<Cell | null>(null);
  const utils = trpc.useUtils();
  
  // Mutation para salvar célula
  const updateCellMutation = trpc.ergos.classificationMatrix.updateClassificationCell.useMutation({
    onSuccess: (data) => {
      toast.success("Célula salva com sucesso!");
      // Invalidar queries para refetch
      utils.ergos.classificationMatrix.getCurrentRiskMatrix.invalidate();
      // Atualizar estado local com dados do servidor
      if (data.cell) {
        setEditingCell(data.cell as Cell);
        onChange(data.cell as Cell);
      }
    },
    onError: (error) => {
      toast.error(`Erro ao salvar: ${error.message}`);
    },
  });

  // Sincronizar estado local quando célula muda
  useEffect(() => {
    if (cell) {
      setEditingCell({ ...cell });
    }
  }, [cell]);

  // Fallback: se não tiver célula, mostrar erro amigável
  if (!isOpen) {
    return null;
  }

  if (!editingCell) {
    return (
      <Card className="rounded-none border-0 shadow-none h-full flex flex-col">
        <CardHeader className="border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Editar Célula</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p>Carregando dados da célula...</p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mt-2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleFieldChange = (field: keyof Cell, value: any) => {
    const updated = { ...editingCell, [field]: value };
    setEditingCell(updated);
    // Atualização instantânea no preview
    onChange(updated);
  };

  const handleCancel = () => {
    setEditingCell(null);
    onClose();
  };

  const handleSave = async () => {
    if (!editingCell || !versionId) {
      toast.error("Dados incompletos para salvar");
      return;
    }

    // Validar campos obrigatórios
    if (!editingCell.riskLevelLabel?.trim()) {
      toast.error("Rótulo do Nível é obrigatório");
      return;
    }

    if (!editingCell.colorHex || !/^#[0-9A-Fa-f]{6}$/.test(editingCell.colorHex)) {
      toast.error("Cor inválida. Use formato #RRGGBB");
      return;
    }

    await updateCellMutation.mutateAsync({
      versionId,
      probabilityCode: editingCell.probabilityCode,
      severityCode: editingCell.severityCode,
      riskLevelLabel: editingCell.riskLevelLabel,
      colorHex: editingCell.colorHex,
      conclusionText: editingCell.conclusionText || "",
    });
  };

  const isSaving = updateCellMutation.isPending;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={handleCancel}
        />
      )}

      {/* Painel lateral (desktop) / Modal (mobile) */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-lg z-50 transform transition-transform duration-300 overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } lg:relative lg:translate-x-0 lg:w-96 lg:border-l lg:shadow-none`}
      >
        <Card className="rounded-none border-0 shadow-none h-full flex flex-col">
          <CardHeader className="border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Editar Célula ({editingCell.probabilityCode} × {editingCell.severityCode})
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="lg:hidden"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="flex-1 space-y-4 pt-4">
            {/* Score (read-only) */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Score (calculado automaticamente: P × S)
              </label>
              <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded font-bold text-lg text-blue-900">
                {editingCell.score}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                O score é recalculado automaticamente ao salvar
              </p>
            </div>

            {/* Probabilidade (read-only) */}
            {probability && (
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Probabilidade
                </label>
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded text-sm">
                  <p className="font-medium">{probability.label}</p>
                  <p className="text-gray-600">{probability.code} (valor: {probability.probabilityValue})</p>
                </div>
              </div>
            )}

            {/* Severidade (read-only) */}
            {severity && (
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Severidade
                </label>
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded text-sm">
                  <p className="font-medium">{severity.label}</p>
                  <p className="text-gray-600">{severity.code} (peso: {severity.weight})</p>
                </div>
              </div>
            )}

            {/* Rótulo do Nível (editável) */}
            <div>
              <label htmlFor="riskLevelLabel" className="text-sm font-semibold text-gray-700">
                Rótulo do Nível *
              </label>
              <Input
                id="riskLevelLabel"
                value={editingCell.riskLevelLabel || ""}
                onChange={(e) => handleFieldChange("riskLevelLabel", e.target.value)}
                placeholder="ex: Baixo, Médio, Alto, Crítico"
                className="mt-1"
                disabled={isSaving}
              />
            </div>

            {/* Conclusão (editável) */}
            <div>
              <label htmlFor="conclusionText" className="text-sm font-semibold text-gray-700">
                Conclusão
              </label>
              <Textarea
                id="conclusionText"
                value={editingCell.conclusionText || ""}
                onChange={(e) => handleFieldChange("conclusionText", e.target.value)}
                placeholder="Descreva a ação recomendada..."
                className="mt-1 resize-none"
                rows={4}
                disabled={isSaving}
              />
            </div>

            {/* Cor (editável) */}
            <div>
              <label htmlFor="colorHex" className="text-sm font-semibold text-gray-700">
                Cor *
              </label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="colorHex"
                  type="text"
                  value={editingCell.colorHex || "#ffffff"}
                  onChange={(e) => handleFieldChange("colorHex", e.target.value)}
                  placeholder="#22c55e"
                  className="flex-1"
                  disabled={isSaving}
                />
                <input
                  type="color"
                  value={editingCell.colorHex || "#ffffff"}
                  onChange={(e) => handleFieldChange("colorHex", e.target.value)}
                  className="w-10 h-10 rounded border-2 cursor-pointer"
                  title="Clique para escolher uma cor"
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* Preview da célula */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Preview</label>
              <div
                className="mt-1 p-4 rounded text-center text-white font-semibold"
                style={{ backgroundColor: editingCell.colorHex || "#ffffff" }}
              >
                <p className="text-lg font-bold">{editingCell.score}</p>
                <p className="text-sm">{editingCell.riskLevelLabel || "-"}</p>
              </div>
            </div>
          </CardContent>

          {/* Botões de ação */}
          <div className="border-t p-4 space-y-2 bg-gray-50">
            <Button
              onClick={handleSave}
              className="w-full"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Célula
                </>
              )}
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              className="w-full"
              disabled={isSaving}
            >
              Cancelar
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
